import React, { useState, useEffect, useRef } from 'react';
import { getImageUrl } from '../utils/imageUtils';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Camera, Edit2, Lock, Unlock, Upload, X, Shield, Crown, MapPin, Calendar, Heart, Eye, UserCircle, Users, CheckCircle, AlertCircle, FolderOpen, Star, ChevronLeft, ChevronRight, Video, Play, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import AlbumsManager from '../components/AlbumsManager';
import TestimonialsManager from '../components/TestimonialsManager';
import PhotoComments from '../components/PhotoComments';
import CityAutocomplete from '../components/CityAutocomplete';
import BlockedUsersList from '../components/BlockedUsersList';

const BODY_TYPES = [
  { value: 'mince', label: 'Mince' },
  { value: 'sportif', label: 'Sportif' },
  { value: 'moyenne', label: 'Dans la moyenne' },
  { value: 'rondeurs', label: 'Quelques rondeurs' },
  { value: 'rond', label: 'Rond' }
];

const EXPERIENCE_LEVELS = [
  { value: 'debutant', label: 'Débutant' },
  { value: 'occasionnel', label: 'Occasionnel' },
  { value: 'experimente', label: 'Expérimenté' }
];

const SEXUAL_PREFERENCES = [
  { value: 'soft', label: 'Soft / Sensuel' },
  { value: 'echangisme', label: 'Échangisme' },
  { value: 'melangisme', label: 'Mélangisme' },
  { value: 'voyeurisme', label: 'Voyeurisme' },
  { value: 'exhib', label: 'Exhibitionnisme' },
  { value: 'bdsm', label: 'BDSM' },
  { value: 'fetichisme', label: 'Fétichisme' },
  { value: 'triolisme', label: 'Triolisme' },
  { value: 'gang_bang', label: 'Gang Bang' }
];

const ETHNICITIES = [
  { value: 'caucasien', label: 'Caucasien' },
  { value: 'africain', label: 'Africain' },
  { value: 'asiatique', label: 'Asiatique' },
  { value: 'metisse', label: 'Métissé' },
  { value: 'latino', label: 'Latino' },
  { value: 'indien', label: 'Indien' },
  { value: 'arabe', label: 'Arabe' },
  { value: 'autre', label: 'Autre' }
];

const EYE_COLORS = [
  { value: 'marrons', label: 'Marrons' },
  { value: 'bleus', label: 'Bleus' },
  { value: 'verts', label: 'Verts' },
  { value: 'gris', label: 'Gris' },
  { value: 'noirs', label: 'Noirs' },
  { value: 'noisette', label: 'Noisette' }
];

const SMOKER_STATUS = [
  { value: 'non_fumeur', label: 'Non fumeur' },
  { value: 'fumeur_occasionnel', label: 'Fumeur occasionnel' },
  { value: 'fumeur', label: 'Fumeur' }
];

const SEXUAL_ORIENTATIONS_WOMAN = [
  { value: 'hetero', label: 'Hétéro' },
  { value: 'bi', label: 'Bi' },
  { value: 'lesbienne', label: 'Lesbienne' }
];

const SEXUAL_ORIENTATIONS_MAN = [
  { value: 'hetero', label: 'Hétéro' },
  { value: 'bi', label: 'Bi' },
  { value: 'gay', label: 'Gay' }
];

export default function ProfilePage() {
  const { user, api, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [accessRequests, setAccessRequests] = useState([]);
  const [grantedUsers, setGrantedUsers] = useState([]);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationPhotos, setVerificationPhotos] = useState([null, null]);
  const [uploadingVerification, setUploadingVerification] = useState(false);
  
  // Album access grants state
  const [albumAccessGrants, setAlbumAccessGrants] = useState([]);
  
  // Photo modal state
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const [selectedPhotoType, setSelectedPhotoType] = useState('public');
  
  // Video modal state
  const [selectedVideo, setSelectedVideo] = useState(null);
  const videoRef = useRef(null);
  
  // Follow stats state
  const [followStats, setFollowStats] = useState({ following: 0, followers: 0 });
  
  // Get default tab from URL params
  const defaultTab = searchParams.get('tab') || 'public';
  const photoIdFromUrl = searchParams.get('photo');

  // Get current photos array based on type
  const getCurrentPhotos = () => {
    if (!profile) return [];
    return selectedPhotoType === 'public' ? (profile.photos || []) : (profile.private_photos || []);
  };

  const selectedPhoto = selectedPhotoIndex !== null ? getCurrentPhotos()[selectedPhotoIndex] : null;

  const openPhotoModal = (index, type = 'public') => {
    setSelectedPhotoType(type);
    setSelectedPhotoIndex(index);
  };

  const closePhotoModal = () => {
    setSelectedPhotoIndex(null);
    // Clear photo param from URL
    searchParams.delete('photo');
    setSearchParams(searchParams);
  };

  const goToPreviousPhoto = (e) => {
    e.stopPropagation();
    const photos = getCurrentPhotos();
    if (selectedPhotoIndex > 0) {
      setSelectedPhotoIndex(selectedPhotoIndex - 1);
    } else {
      setSelectedPhotoIndex(photos.length - 1);
    }
  };

  const goToNextPhoto = (e) => {
    e.stopPropagation();
    const photos = getCurrentPhotos();
    if (selectedPhotoIndex < photos.length - 1) {
      setSelectedPhotoIndex(selectedPhotoIndex + 1);
    } else {
      setSelectedPhotoIndex(0);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchAccessRequests();
    fetchGrantedUsers();
    fetchVerificationStatus();
    fetchAlbumAccessGrants();
    fetchFollowStats();
  }, []);

  // Open photo from URL param after profile loads
  useEffect(() => {
    if (profile && photoIdFromUrl) {
      const photoIndex = profile.photos?.findIndex(p => p.id === photoIdFromUrl);
      if (photoIndex !== -1) {
        openPhotoModal(photoIndex, 'public');
      } else {
        const privateIndex = profile.private_photos?.findIndex(p => p.id === photoIdFromUrl);
        if (privateIndex !== -1) {
          openPhotoModal(privateIndex, 'private');
        }
      }
    }
  }, [profile, photoIdFromUrl]);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/profile');
      setProfile(response.data);
      setEditData(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setProfile(null);
      } else {
        toast.error('Erreur lors du chargement du profil');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAccessRequests = async () => {
    try {
      const response = await api.get('/photos/access-requests');
      setAccessRequests(response.data);
    } catch (error) {
      console.error('Error fetching access requests:', error);
    }
  };

  const fetchGrantedUsers = async () => {
    try {
      const response = await api.get('/photos/granted-users');
      setGrantedUsers(response.data);
    } catch (error) {
      console.error('Error fetching granted users:', error);
    }
  };

  const fetchVerificationStatus = async () => {
    try {
      const response = await api.get('/verification/status');
      setVerificationStatus(response.data);
    } catch (error) {
      console.error('Error fetching verification status:', error);
    }
  };

  const fetchAlbumAccessGrants = async () => {
    try {
      const response = await api.get('/album-access/grants');
      setAlbumAccessGrants(response.data);
    } catch (error) {
      console.error('Error fetching album access grants:', error);
    }
  };

  const fetchFollowStats = async () => {
    try {
      const [followingRes, followersRes] = await Promise.all([
        api.get('/following?limit=1'),
        api.get('/followers?limit=1')
      ]);
      setFollowStats({
        following: followingRes.data.total || 0,
        followers: followersRes.data.total || 0
      });
    } catch (error) {
      console.error('Error fetching follow stats:', error);
    }
  };

  const revokeAlbumAccess = async (targetUserId) => {
    try {
      await api.delete(`/album-access/revoke/${targetUserId}`);
      toast.success('Accès révoqué');
      fetchAlbumAccessGrants();
    } catch (error) {
      toast.error('Erreur lors de la révocation');
    }
  };

  const handleSave = async () => {
    try {
      await api.put('/profile', editData);
      setProfile(editData);
      setEditing(false);
      toast.success('Profil mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handlePhotoUpload = async (e, isPrivate = false, isProfilePicture = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('is_private', isPrivate.toString());
    formData.append('is_profile_picture', isProfilePicture.toString());

    try {
      await api.post('/photos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(isProfilePicture ? 'Photo de profil mise à jour' : 'Photo ajoutée');
      fetchProfile();
      refreshUser();
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleVideoUpload = async (e, isPrivate = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('La vidéo ne doit pas dépasser 100 Mo');
      return;
    }

    setUploadingVideo(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('is_private', isPrivate.toString());

    try {
      await api.post('/videos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Vidéo ajoutée');
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload de la vidéo');
    } finally {
      setUploadingVideo(false);
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette vidéo ?')) return;
    
    try {
      await api.delete(`/videos/${videoId}`);
      toast.success('Vidéo supprimée');
      fetchProfile();
      setSelectedVideo(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openVideoModal = (video) => {
    setSelectedVideo(video);
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const setAsProfilePicture = async (photoId, photoUrl) => {
    try {
      await api.put(`/photos/set-profile-picture?photo_id=${photoId}&photo_url=${encodeURIComponent(photoUrl)}`);
      toast.success('Photo de profil mise à jour');
      fetchProfile();
      refreshUser();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const respondToAccessRequest = async (requestId, granted) => {
    try {
      await api.post('/photos/respond-access-request', { request_id: requestId, granted });
      toast.success(granted ? 'Accès accordé' : 'Demande refusée');
      fetchAccessRequests();
      fetchGrantedUsers();
    } catch (error) {
      toast.error('Erreur lors de la réponse');
    }
  };

  const revokeAccess = async (userId) => {
    try {
      await api.post('/photos/revoke-access', { photo_id: '', user_id: userId });
      toast.success('Accès révoqué');
      fetchGrantedUsers();
    } catch (error) {
      toast.error('Erreur lors de la révocation');
    }
  };

  const handleVerificationPhotoChange = (index, file) => {
    const newPhotos = [...verificationPhotos];
    newPhotos[index] = file;
    setVerificationPhotos(newPhotos);
  };

  const submitVerification = async () => {
    if (!verificationPhotos[0] || !verificationPhotos[1]) {
      toast.error('Veuillez ajouter les deux photos');
      return;
    }

    setUploadingVerification(true);
    const formData = new FormData();
    formData.append('photo1', verificationPhotos[0]);
    formData.append('photo2', verificationPhotos[1]);

    try {
      await api.post('/verification/upload-photos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photos envoyées pour vérification');
      setShowVerificationModal(false);
      setVerificationPhotos([null, null]);
      fetchVerificationStatus();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'envoi';
      toast.error(message);
    } finally {
      setUploadingVerification(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark pt-20 md:pt-24 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-dark pt-20 md:pt-24 flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-xl md:text-2xl font-serif gold-gradient-text mb-4">Profil non créé</h2>
          <p className="text-gray-400">Veuillez compléter votre inscription</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pt-20 md:pt-24 pb-20 md:pb-12" data-testid="profile-page">
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        {/* Access Requests Notification */}
        {accessRequests.length > 0 && (
          <div className="glass-card p-3 md:p-4 mb-4 md:mb-6 border-l-4 border-gold">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-gold flex-shrink-0" />
              <span className="text-white text-sm md:text-base">{accessRequests.length} demande(s) d'accès à vos photos privées</span>
            </div>
          </div>
        )}

        {/* Header Section */}
        <div className="glass-card p-4 md:p-8 mb-6 md:mb-8">
          <div className="flex flex-col items-center md:flex-row md:items-start gap-6 md:gap-8">
            {/* Profile Picture */}
            <div className="relative group">
              <div className="w-28 h-28 md:w-40 md:h-40 rounded-full overflow-hidden bg-dark-subtle border-4 border-gold/30">
                {profile.profile_picture ? (
                  <img 
                    src={getImageUrl(profile.profile_picture)} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Camera className="w-8 h-8 md:w-12 md:h-12 text-gray-500" />
                  </div>
                )}
              </div>
              <label className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-full">
                <Upload className="w-6 h-6 md:w-8 md:h-8 text-gold" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handlePhotoUpload(e, false, true)}
                  className="hidden"
                  disabled={uploadingPhoto}
                />
              </label>
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h1 className="text-3xl font-serif gold-gradient-text">{profile.pseudo}</h1>
                {user.is_ambassador && (
                  <span className="flex items-center gap-1 px-2 py-1 bg-gold text-dark text-xs font-semibold">
                    <Crown className="w-3 h-3" /> Ambassadeur
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-4">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" /> {profile.location}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> {profile.age} ans
                </span>
                <span className="capitalize">{profile.profile_type}</span>
                <span className="capitalize">{profile.body_type}</span>
              </div>

              <div className="flex gap-4 text-sm">
                <div className="text-center">
                  <p className="text-gold font-semibold">{profile.photos?.length || 0}</p>
                  <p className="text-gray-500">Photos</p>
                </div>
                <div className="text-center">
                  <p className="text-gold font-semibold">{profile.likes_count || 0}</p>
                  <p className="text-gray-500">Likes</p>
                </div>
                <Link to="/relations" className="text-center hover:bg-white/5 px-2 py-1 rounded transition-colors" data-testid="profile-following-link">
                  <p className="text-gold font-semibold">{followStats.following}</p>
                  <p className="text-gray-500">Suivis</p>
                </Link>
                <Link to="/relations" className="text-center hover:bg-white/5 px-2 py-1 rounded transition-colors" data-testid="profile-followers-link">
                  <p className="text-gold font-semibold">{followStats.followers}</p>
                  <p className="text-gray-500">Followers</p>
                </Link>
              </div>
            </div>

            {/* Edit Button */}
            <Dialog open={editing} onOpenChange={setEditing}>
              <DialogTrigger asChild>
                <Button className="btn-outline-gold flex items-center gap-2" data-testid="edit-profile-btn">
                  <Edit2 className="w-4 h-4" /> Modifier
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-dark-paper border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl gold-gradient-text">Modifier le profil</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label className="text-gray-400">Description</Label>
                    <Textarea
                      value={editData.description || ''}
                      onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                      className="bg-transparent border-white/20 text-white min-h-[100px]"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-400">Lieu</Label>
                      <CityAutocomplete
                        value={editData.location || ''}
                        onChange={(value) => setEditData(prev => ({ ...prev, location: value }))}
                        onSelect={(locationData) => {
                          if (locationData) {
                            setEditData({
                              ...editData,
                              location: locationData.location,
                              city: locationData.city,
                              department: locationData.department,
                              region: locationData.region,
                              coordinates: locationData.coordinates
                            });
                          }
                        }}
                        placeholder="Entrez votre ville"
                        data-testid="edit-location"
                      />
                    </div>
                    {/* Age - only for non-couple profiles */}
                    {editData.profile_type !== 'couple' && (
                      <div className="space-y-2">
                        <Label className="text-gray-400">Âge</Label>
                        <Input
                          type="number"
                          value={editData.age || ''}
                          onChange={(e) => setEditData(prev => ({ ...prev, age: parseInt(e.target.value) }))}
                          className="input-gold"
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Physical attributes - only for non-couple profiles */}
                  {editData.profile_type !== 'couple' && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-gray-400">Physique</Label>
                          <Select
                            value={editData.body_type}
                            onValueChange={(value) => setEditData(prev => ({ ...prev, body_type: value }))}
                          >
                            <SelectTrigger className="bg-transparent border-white/20 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-dark-paper border-white/10">
                              {BODY_TYPES.map(type => (
                                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-gray-400">Expérience</Label>
                          <Select
                            value={editData.experience_level}
                            onValueChange={(value) => setEditData(prev => ({ ...prev, experience_level: value }))}
                          >
                            <SelectTrigger className="bg-transparent border-white/20 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-dark-paper border-white/10">
                              {EXPERIENCE_LEVELS.map(type => (
                                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </>
                  )}
                  
                  {/* Height and Ethnicity - only for non-couple profiles */}
                  {editData.profile_type !== 'couple' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-400">Taille (cm)</Label>
                        <Input
                          type="number"
                          min="100"
                          max="250"
                          value={editData.height || ''}
                          onChange={(e) => setEditData(prev => ({ ...prev, height: parseInt(e.target.value) || null }))}
                          className="input-gold"
                          placeholder="Ex: 175"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-400">Origine</Label>
                        <Select
                          value={editData.ethnicity || ''}
                          onValueChange={(value) => setEditData(prev => ({ ...prev, ethnicity: value }))}
                        >
                          <SelectTrigger className="bg-transparent border-white/20 text-white">
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent className="bg-dark-paper border-white/10">
                            {ETHNICITIES.map(type => (
                              <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  
                  {/* Sexual Preferences */}
                  <div className="space-y-2">
                    <Label className="text-gray-400">Préférences (plusieurs choix possibles)</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {SEXUAL_PREFERENCES.map(pref => (
                        <label
                          key={pref.value}
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                            (editData.sexual_preferences || []).includes(pref.value)
                              ? 'bg-gold/20 text-gold border border-gold/50'
                              : 'bg-dark-subtle text-gray-400 border border-white/10 hover:border-white/20'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={(editData.sexual_preferences || []).includes(pref.value)}
                            onChange={(e) => {
                              const current = editData.sexual_preferences || [];
                              if (e.target.checked) {
                                setEditData(prev => ({ ...prev, sexual_preferences: [...current, pref.value] }));
                              } else {
                                setEditData(prev => ({ ...prev, sexual_preferences: current.filter(p => p !== pref.value) }));
                              }
                            }}
                            className="hidden"
                          />
                          <span className="text-sm">{pref.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Couple Section - Only for couple profiles */}
                  {editData.profile_type === 'couple' && (
                    <div className="space-y-6 pt-4 border-t border-white/10">
                      <h3 className="text-gold font-semibold flex items-center gap-2">
                        <Users className="w-5 h-5" /> Détails du couple
                      </h3>
                      
                      {/* Elle - Woman */}
                      <div className="p-4 bg-pink-500/10 border border-pink-500/30 rounded-lg">
                        <h4 className="text-pink-400 font-semibold mb-4 flex items-center gap-2">
                          <span className="text-lg">♀</span> Elle
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-gray-400">Âge</Label>
                            <Input
                              type="number"
                              min="18"
                              max="99"
                              value={editData.couple_woman?.age || ''}
                              onChange={(e) => setEditData({
                                ...editData,
                                couple_woman: { ...editData.couple_woman, age: parseInt(e.target.value) }
                              })}
                              className="input-gold"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-400">Taille (cm)</Label>
                            <Input
                              type="number"
                              min="140"
                              max="220"
                              value={editData.couple_woman?.height || ''}
                              onChange={(e) => setEditData({
                                ...editData,
                                couple_woman: { ...editData.couple_woman, height: parseInt(e.target.value) }
                              })}
                              className="input-gold"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div className="space-y-2">
                            <Label className="text-gray-400">Physique</Label>
                            <Select
                              value={editData.couple_woman?.body_type || ''}
                              onValueChange={(value) => setEditData({
                                ...editData,
                                couple_woman: { ...editData.couple_woman, body_type: value }
                              })}
                            >
                              <SelectTrigger className="bg-transparent border-white/20 text-white">
                                <SelectValue placeholder="Sélectionner" />
                              </SelectTrigger>
                              <SelectContent className="bg-dark-paper border-white/10">
                                {BODY_TYPES.map(type => (
                                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-400">Origine</Label>
                            <Select
                              value={editData.couple_woman?.ethnicity || ''}
                              onValueChange={(value) => setEditData({
                                ...editData,
                                couple_woman: { ...editData.couple_woman, ethnicity: value }
                              })}
                            >
                              <SelectTrigger className="bg-transparent border-white/20 text-white">
                                <SelectValue placeholder="Sélectionner" />
                              </SelectTrigger>
                              <SelectContent className="bg-dark-paper border-white/10">
                                {ETHNICITIES.map(type => (
                                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div className="space-y-2">
                            <Label className="text-gray-400">Yeux</Label>
                            <Select
                              value={editData.couple_woman?.eye_color || ''}
                              onValueChange={(value) => setEditData({
                                ...editData,
                                couple_woman: { ...editData.couple_woman, eye_color: value }
                              })}
                            >
                              <SelectTrigger className="bg-transparent border-white/20 text-white">
                                <SelectValue placeholder="Sélectionner" />
                              </SelectTrigger>
                              <SelectContent className="bg-dark-paper border-white/10">
                                {EYE_COLORS.map(type => (
                                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-400">Orientation</Label>
                            <Select
                              value={editData.couple_woman?.sexual_orientation || ''}
                              onValueChange={(value) => setEditData({
                                ...editData,
                                couple_woman: { ...editData.couple_woman, sexual_orientation: value }
                              })}
                            >
                              <SelectTrigger className="bg-transparent border-white/20 text-white">
                                <SelectValue placeholder="Sélectionner" />
                              </SelectTrigger>
                              <SelectContent className="bg-dark-paper border-white/10">
                                {SEXUAL_ORIENTATIONS_WOMAN.map(type => (
                                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div className="space-y-2">
                            <Label className="text-gray-400">Expérience</Label>
                            <Select
                              value={editData.couple_woman?.experience_level || ''}
                              onValueChange={(value) => setEditData({
                                ...editData,
                                couple_woman: { ...editData.couple_woman, experience_level: value }
                              })}
                            >
                              <SelectTrigger className="bg-transparent border-white/20 text-white">
                                <SelectValue placeholder="Sélectionner" />
                              </SelectTrigger>
                              <SelectContent className="bg-dark-paper border-white/10">
                                {EXPERIENCE_LEVELS.map(type => (
                                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-400">Fumeur</Label>
                            <Select
                              value={editData.couple_woman?.smoker || ''}
                              onValueChange={(value) => setEditData({
                                ...editData,
                                couple_woman: { ...editData.couple_woman, smoker: value }
                              })}
                            >
                              <SelectTrigger className="bg-transparent border-white/20 text-white">
                                <SelectValue placeholder="Sélectionner" />
                              </SelectTrigger>
                              <SelectContent className="bg-dark-paper border-white/10">
                                {SMOKER_STATUS.map(type => (
                                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                      
                      {/* Lui - Man */}
                      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <h4 className="text-blue-400 font-semibold mb-4 flex items-center gap-2">
                          <span className="text-lg">♂</span> Lui
                        </h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-gray-400">Âge</Label>
                            <Input
                              type="number"
                              min="18"
                              max="99"
                              value={editData.couple_man?.age || ''}
                              onChange={(e) => setEditData({
                                ...editData,
                                couple_man: { ...editData.couple_man, age: parseInt(e.target.value) }
                              })}
                              className="input-gold"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-400">Taille (cm)</Label>
                            <Input
                              type="number"
                              min="140"
                              max="220"
                              value={editData.couple_man?.height || ''}
                              onChange={(e) => setEditData({
                                ...editData,
                                couple_man: { ...editData.couple_man, height: parseInt(e.target.value) }
                              })}
                              className="input-gold"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div className="space-y-2">
                            <Label className="text-gray-400">Physique</Label>
                            <Select
                              value={editData.couple_man?.body_type || ''}
                              onValueChange={(value) => setEditData({
                                ...editData,
                                couple_man: { ...editData.couple_man, body_type: value }
                              })}
                            >
                              <SelectTrigger className="bg-transparent border-white/20 text-white">
                                <SelectValue placeholder="Sélectionner" />
                              </SelectTrigger>
                              <SelectContent className="bg-dark-paper border-white/10">
                                {BODY_TYPES.map(type => (
                                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-400">Origine</Label>
                            <Select
                              value={editData.couple_man?.ethnicity || ''}
                              onValueChange={(value) => setEditData({
                                ...editData,
                                couple_man: { ...editData.couple_man, ethnicity: value }
                              })}
                            >
                              <SelectTrigger className="bg-transparent border-white/20 text-white">
                                <SelectValue placeholder="Sélectionner" />
                              </SelectTrigger>
                              <SelectContent className="bg-dark-paper border-white/10">
                                {ETHNICITIES.map(type => (
                                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div className="space-y-2">
                            <Label className="text-gray-400">Yeux</Label>
                            <Select
                              value={editData.couple_man?.eye_color || ''}
                              onValueChange={(value) => setEditData({
                                ...editData,
                                couple_man: { ...editData.couple_man, eye_color: value }
                              })}
                            >
                              <SelectTrigger className="bg-transparent border-white/20 text-white">
                                <SelectValue placeholder="Sélectionner" />
                              </SelectTrigger>
                              <SelectContent className="bg-dark-paper border-white/10">
                                {EYE_COLORS.map(type => (
                                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-400">Orientation</Label>
                            <Select
                              value={editData.couple_man?.sexual_orientation || ''}
                              onValueChange={(value) => setEditData({
                                ...editData,
                                couple_man: { ...editData.couple_man, sexual_orientation: value }
                              })}
                            >
                              <SelectTrigger className="bg-transparent border-white/20 text-white">
                                <SelectValue placeholder="Sélectionner" />
                              </SelectTrigger>
                              <SelectContent className="bg-dark-paper border-white/10">
                                {SEXUAL_ORIENTATIONS_MAN.map(type => (
                                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div className="space-y-2">
                            <Label className="text-gray-400">Expérience</Label>
                            <Select
                              value={editData.couple_man?.experience_level || ''}
                              onValueChange={(value) => setEditData({
                                ...editData,
                                couple_man: { ...editData.couple_man, experience_level: value }
                              })}
                            >
                              <SelectTrigger className="bg-transparent border-white/20 text-white">
                                <SelectValue placeholder="Sélectionner" />
                              </SelectTrigger>
                              <SelectContent className="bg-dark-paper border-white/10">
                                {EXPERIENCE_LEVELS.map(type => (
                                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-gray-400">Fumeur</Label>
                            <Select
                              value={editData.couple_man?.smoker || ''}
                              onValueChange={(value) => setEditData({
                                ...editData,
                                couple_man: { ...editData.couple_man, smoker: value }
                              })}
                            >
                              <SelectTrigger className="bg-transparent border-white/20 text-white">
                                <SelectValue placeholder="Sélectionner" />
                              </SelectTrigger>
                              <SelectContent className="bg-dark-paper border-white/10">
                                {SMOKER_STATUS.map(type => (
                                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <Button onClick={handleSave} className="w-full btn-gold" data-testid="save-profile-btn">
                    Enregistrer
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Description */}
          <div className="mt-8 pt-8 border-t border-white/10">
            <h3 className="text-gold text-sm uppercase tracking-widest mb-3">À propos</h3>
            <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{profile.description}</p>
          </div>

          {/* Preferences */}
          {profile.sexual_preferences?.length > 0 && (
            <div className="mt-6">
              <h3 className="text-gold text-sm uppercase tracking-widest mb-3">Préférences</h3>
              <div className="flex flex-wrap gap-2">
                {profile.sexual_preferences.map(pref => (
                  <span key={pref} className="px-3 py-1 bg-gold/10 text-gold text-xs border border-gold/30">
                    {pref}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Photos Section */}
        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="bg-dark-paper border border-white/10 w-full h-auto flex-wrap gap-1 p-1">
            <TabsTrigger value="gallery" className="data-[state=active]:bg-gold/20 data-[state=active]:text-gold text-xs sm:text-sm px-2 sm:px-3 py-1.5" data-testid="gallery-tab">
              <FolderOpen className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> 
              <span className="hidden xs:inline">Galerie</span>
              <span className="xs:hidden">Photos</span>
            </TabsTrigger>
            <TabsTrigger value="access" className="data-[state=active]:bg-gold/20 data-[state=active]:text-gold text-xs sm:text-sm px-2 sm:px-3 py-1.5">
              <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> 
              <span className="hidden sm:inline">Suivi des accès</span>
              <span className="sm:hidden">Accès</span>
              {albumAccessGrants.length > 0 && (
                <span className="ml-1 sm:ml-2 px-1 sm:px-1.5 py-0.5 bg-gold text-dark text-xs rounded-full">{albumAccessGrants.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="data-[state=active]:bg-gold/20 data-[state=active]:text-gold text-xs sm:text-sm px-2 sm:px-3 py-1.5" data-testid="testimonials-tab">
              <Star className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Témoignages
            </TabsTrigger>
            <TabsTrigger value="blocked" className="data-[state=active]:bg-gold/20 data-[state=active]:text-gold text-xs sm:text-sm px-2 sm:px-3 py-1.5" data-testid="blocked-tab">
              <Lock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Bloqués
            </TabsTrigger>
          </TabsList>

          {/* Gallery Tab - Contains Albums, Public Photos, Private Photos */}
          <TabsContent value="gallery" className="mt-6">
            <AlbumsManager />
          </TabsContent>

          {/* Access Management Tab */}
          <TabsContent value="access" className="mt-6">
            <div className="space-y-6">
              {/* Album Access Grants - NEW SECTION */}
              <div className="glass-card p-6">
                <h3 className="text-gold text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
                  <FolderOpen className="w-4 h-4" /> Accès à mes albums privés ({albumAccessGrants.length})
                </h3>
                {albumAccessGrants.length === 0 ? (
                  <p className="text-gray-500 text-sm">Aucun membre n'a accès à vos albums privés</p>
                ) : (
                  <div className="space-y-3">
                    {albumAccessGrants.map((grant) => (
                      <div key={grant.id} className="flex items-center justify-between p-4 bg-dark-subtle rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center overflow-hidden">
                            {grant.target_user?.profile_picture ? (
                              <img 
                                src={getImageUrl(grant.target_user.profile_picture)}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="text-gold text-lg font-semibold">{grant.target_user?.pseudo?.charAt(0)}</span>
                            )}
                          </div>
                          <div>
                            <p className="text-white font-medium">{grant.target_user?.pseudo}</p>
                            <div className="flex items-center gap-2 mt-1">
                              {grant.share_all ? (
                                <span className="text-xs text-gray-400">Tous les albums privés</span>
                              ) : (
                                <span className="text-xs text-gray-400">{grant.album_ids?.length} album(s)</span>
                              )}
                              <span className="text-gray-600">•</span>
                              <span className={`text-xs flex items-center gap-1 ${
                                grant.remaining_seconds === null 
                                  ? 'text-green-400' 
                                  : grant.remaining_seconds < 3600 
                                    ? 'text-red-400' 
                                    : 'text-gold'
                              }`}>
                                {grant.remaining_seconds !== null && (
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                )}
                                {grant.remaining_text}
                              </span>
                            </div>
                          </div>
                        </div>
                        <Button 
                          size="sm"
                          variant="outline" 
                          onClick={() => revokeAlbumAccess(grant.target_user_id)}
                          className="border-red-500 text-red-500 hover:bg-red-500/10 text-xs px-3"
                        >
                          <Lock className="w-3 h-3 mr-1" /> Révoquer
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="testimonials" className="mt-6">
            <TestimonialsManager />
          </TabsContent>

          <TabsContent value="blocked" className="mt-6">
            <div className="glass-card p-6">
              <BlockedUsersList />
            </div>
          </TabsContent>
        </Tabs>

        {/* Photo Modal with Navigation and Comments */}
        <Dialog open={selectedPhotoIndex !== null} onOpenChange={(open) => !open && closePhotoModal()}>
          <DialogContent className="bg-dark-paper border-white/10 max-w-5xl max-h-[90vh] overflow-hidden p-0">
            {selectedPhoto && (
              <div className="flex flex-col md:flex-row h-full">
                {/* Photo with Navigation */}
                <div className="relative flex-1 flex items-center justify-center bg-black min-h-[300px] md:min-h-[500px]">
                  {/* Previous button */}
                  {getCurrentPhotos().length > 1 && (
                    <button
                      onClick={goToPreviousPhoto}
                      className="absolute left-2 md:left-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                    >
                      <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" />
                    </button>
                  )}
                  
                  {/* Photo */}
                  <img
                    src={getImageUrl(selectedPhoto.url)}
                    alt="Photo"
                    className="max-w-full max-h-[50vh] md:max-h-[80vh] object-contain"
                  />
                  
                  {/* Next button */}
                  {getCurrentPhotos().length > 1 && (
                    <button
                      onClick={goToNextPhoto}
                      className="absolute right-2 md:right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
                    >
                      <ChevronRight className="w-6 h-6 md:w-8 md:h-8" />
                    </button>
                  )}
                  
                  {/* Photo counter */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 rounded-full text-white text-sm">
                    {selectedPhotoIndex + 1} / {getCurrentPhotos().length}
                  </div>
                </div>
                
                {/* Comments section */}
                <div className="w-full md:w-80 lg:w-96 flex flex-col p-4 md:p-6 border-t md:border-t-0 md:border-l border-white/10 max-h-[40vh] md:max-h-none overflow-y-auto">
                  {/* Photo info */}
                  <div className="flex items-center justify-between pb-4 border-b border-white/10 mb-4">
                    <span className="flex items-center gap-2 text-gray-400">
                      <Heart className="w-4 h-4" /> {selectedPhoto.likes || 0} likes
                    </span>
                    <span className="text-xs text-gray-500">
                      {selectedPhotoType === 'private' && <Lock className="w-3 h-3 inline mr-1" />}
                      {selectedPhotoType === 'private' ? 'Photo privée' : 'Photo publique'}
                    </span>
                  </div>
                  
                  {/* Comments */}
                  <PhotoComments 
                    photoId={selectedPhoto.id} 
                    photoOwnerId={user.id}
                  />
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Video Modal */}
        <Dialog open={selectedVideo !== null} onOpenChange={(open) => !open && closeVideoModal()}>
          <DialogContent className="bg-dark-paper border-white/10 max-w-4xl max-h-[90vh] overflow-hidden p-0">
            {selectedVideo && (
              <div className="flex flex-col">
                {/* Video Player */}
                <div className="relative bg-black flex items-center justify-center">
                  <video
                    ref={videoRef}
                    src={getImageUrl(selectedVideo.url)}
                    className="max-w-full max-h-[70vh] object-contain"
                    controls
                    autoPlay
                  />
                </div>
                
                {/* Video info and actions */}
                <div className="p-4 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-2 text-gray-400">
                        <Heart className="w-4 h-4" /> {selectedVideo.likes || 0} likes
                      </span>
                      <span className="text-xs text-gray-500">
                        {selectedVideo.is_private && <Lock className="w-3 h-3 inline mr-1" />}
                        Ajoutée le {new Date(selectedVideo.uploaded_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    <Button
                      onClick={() => deleteVideo(selectedVideo.id)}
                      variant="ghost"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Supprimer
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
